
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Search,
  MapPin,
  Bed,
  Bath,
  Car,
  Square,
  ArrowLeft,
  Phone,
} from "lucide-react";
import { getPublicProperties, startChatbotSession } from "../../api/publicApi";
import { cachedApiCall, cacheKeys } from "../../lib/cache";
import LazyImage from "../common/LazyImage";

export default function PropertyViewListing() {
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [toastShown, setToastShown] = useState(
    () => localStorage.getItem("property_toast_shown") === "true"
  );

  const [sellerLoading, setSellerLoading] = useState(false);

  const [filters, setFilters] = useState({
    listingType: "All Types",
    propertyType: "Any Type",
    priceMin: "",
    priceMax: "",
    minBeds: "Any",
    suburb: ""
  });

  // Basic loader (params optional) with caching
  const loadProperties = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Use cached API call - 3 minute cache for public properties
      const res = await cachedApiCall(
        cacheKeys.publicProperties(params),
        () => getPublicProperties(params),
        3 * 60 * 1000
      );

      const data = res.data;
      let normalized = [];
      if (Array.isArray(data)) normalized = data;
      else if (Array.isArray(data.results)) normalized = data.results;
      else if (data?.data && Array.isArray(data.data)) normalized = data.data;
      else {
        console.warn("⚠️ Unexpected data format:", data);
      }

      // Normalize property data to handle both CRM and REA properties
      normalized = normalized.map(prop => ({
        ...prop,
        // Ensure consistent field names
        title: prop.title || prop.headline || 'Untitled Property',
        address: prop.address || prop.location || 'No address available',
        bedrooms: prop.beds || prop.bedrooms || 0,
        bathrooms: prop.baths || prop.bathrooms || 0,
        car_spaces: prop.parkings || prop.car_spaces || 0,
        // Handle image arrays
        images: Array.isArray(prop.images) ? prop.images :
          (prop.images?.length ? prop.images : []),
        // Status/listing type
        status: prop.status || prop.listing_type || 'Active',
        // Price
        price: prop.price || 0,
      }));

      setProperties(normalized);

      if (!toastShown) {
        // toast.success("Properties loaded successfully!");
        setToastShown(true);
        // localStorage.setItem("property_toast_shown", "true");
      }
    } catch (err) {
      console.error("Error fetching properties:", err);
      setError("Failed to load properties. Please try again later.");
      toast.error("Failed to load property listings.");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced live search (typing)
  useEffect(() => {
    const t = setTimeout(() => {
      handleApplyFilters();
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle filter UI changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Build filter params from current filter state
  const buildFilterParams = () => {
    const params = {};

    // Listing Type filter - Map to API expected values
    if (filters.listingType && filters.listingType !== "All Types") {
      let val = filters.listingType.toLowerCase();
      if (val === "for sale") val = "sale";
      if (val === "for rent") val = "rental";
      params.listing_type = val;
    }

    // Property Type filter
    if (filters.propertyType && filters.propertyType !== "Any Type") {
      params.property_type = filters.propertyType;
    }

    // Price range filters
    if (filters.priceMin) {
      params.min_price = filters.priceMin;
    }
    if (filters.priceMax) {
      params.max_price = filters.priceMax;
    }

    // Bedrooms filter
    if (filters.minBeds && filters.minBeds !== "Any") {
      params.beds = filters.minBeds;
    }

    // Suburb filter (mapped to 'city' in backend)
    if (filters.suburb?.trim()) {
      params.city = filters.suburb.trim();
    }

    // Add search query if present
    if (searchQuery?.trim()) {
      params.search = searchQuery.trim();
    }

    return params;
  };

  // APPLY FILTERS - fetch properties with current filter params
  const handleApplyFilters = async () => {
    const params = buildFilterParams();
    await loadProperties(params);
  };

  // CLEAR FILTERS - reset all filters and reload properties
  const handleClearFilters = async () => {
    setFilters({
      listingType: "All Types",
      propertyType: "Any Type",
      priceMin: "",
      priceMax: "",
      minBeds: "Any",
      suburb: ""
    });
    setSearchQuery("");
    // loadProperties is automatically triggered by useEffects monitoring filters and searchQuery
  };

  // Auto-apply filters when they change (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      handleApplyFilters();
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Seller Enquiry -> POST chatbot/start/ { flow: "owner" } then navigate
  const handleSellerEnquiry = async () => {
    try {
      setSellerLoading(true);
      const res = await startChatbotSession({ flow: "owner" });

      const data = res?.data ?? {};
      const sessionId =
        data.session_id ??
        data.sessionId ??
        data.id ??
        data.session?.id ??
        data.data?.session_id ??
        null;

      const message =
        data.message ||
        "What's the property address you're considering selling?";

      // Navigate to your voice agent page, passing initial prompt + session id
      navigate("/property-voice-agent", {
        state: {
          sessionId: sessionId ?? undefined,
          sessionToken: data.session_token ?? undefined,
          voiceMessage: message,
        },
      });

      toast.success("Seller session started.");
    } catch (err) {
      console.error("Error starting seller session:", err);
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to start seller session.";
      toast.error(detail);
    } finally {
      setSellerLoading(false);
    }
  };

  // Contact Us → Navigate to contact form
  const handleContactUs = () => {
    navigate("/contact-agent", {
      state: { property: null } // No specific property for general contact
    });
  };

  // Get in Touch for specific property
  const handleGetInTouch = (property, event) => {
    event.stopPropagation(); // Prevent card click navigation
    navigate("/contact-agent", {
      state: { property: property } // Pass the specific property
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="p-6 space-y-8">
        {/* Top Bar: Back (left) + Actions (right) */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/login")}
            className="min-w-32 bg-[#55C5D0] text-white px-4 py-3 rounded-lg hover:bg-[#4AADB7] font-semibold shadow-md flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Login
          </button>

          <div className="flex items-center gap-3">
            {/* <button
              onClick={handleSellerEnquiry}
              disabled={sellerLoading}
              className={`min-w-32 text-white px-4 py-3 rounded-lg font-semibold shadow-md ${
                sellerLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#55C5D0] hover:bg-[#4AADB7]"
              }`}
            >
              {sellerLoading ? "Starting…" : "Seller Enquiry"}
            </button> */}

            <button
              onClick={handleContactUs}
              className="min-w-28 px-4 py-3 rounded-lg font-semibold shadow-md border border-[#55C5D0] text-[#55C5D0] bg-white hover:bg-[#ecfeff] transition-colors"
            >
              Contact Us
            </button>
          </div>
        </div>

        {/* Top Banner */}
        <div className="bg-white shadow-sm w-full rounded-xl overflow-hidden relative">
          <div
            className="px-10 py-10 relative w-full"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1605146769289-440113cc3d00?auto=format&fit=crop&w=2070&q=80')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gray-900/50 pointer-events-none"></div>

            <div className="relative z-10 text-white max-w-full text-left">
              <h1 className="text-3xl sm:text-4xl text-[#55C5D0] mb-3 font-semibold">
                Property Intelligence Portal
              </h1>
              <p className="text-sm sm:text-base font-medium mb-6 max-w-2xl">
                Search unified property, owner, and market data across Melbourne
                with AI-powered insights.
              </p>

              {/* Search */}
              <div className="flex w-full gap-3 max-w-3xl">
                <input
                  type="text"
                  aria-label="Search properties"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by property name..."
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                />
                <button
                  onClick={() => loadProperties({ search: searchQuery })}
                  title="Search"
                  className="bg-[#55C5D0] hover:bg-[#4AADB7] text-white px-6 py-3 rounded-lg flex items-center justify-center"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters + List */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Filters */}
          <aside className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-lg font-semibold mb-4">Filter Properties</h3>

              <div className="space-y-5">
                {/* Suburb */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Suburb / City
                  </label>
                  <input
                    name="suburb"
                    type="text"
                    placeholder="e.g. Melbourne"
                    value={filters.suburb}
                    onChange={handleFilterChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent outline-none"
                  />
                </div>

                {/* Listing Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Listing Type
                  </label>
                  <select
                    name="listingType"
                    value={filters.listingType}
                    onChange={handleFilterChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option>All Types</option>
                    <option>For Sale</option>
                    <option>For Rent</option>
                    <option>Pending</option>
                  </select>
                </div>

                {/* Property Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Type
                  </label>
                  <select
                    name="propertyType"
                    value={filters.propertyType}
                    onChange={handleFilterChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option>Any Type</option>
                    <option>House</option>
                    <option>Apartment</option>
                    <option>Townhouse</option>
                    <option>Unit</option>
                    <option>Villa</option>
                    <option>Land</option>
                    <option>Other</option>
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price Range
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      name="priceMin"
                      type="number"
                      placeholder="Min"
                      value={filters.priceMin}
                      onChange={handleFilterChange}
                      className="p-3 border border-gray-300 rounded-lg"
                    />
                    <input
                      name="priceMax"
                      type="number"
                      placeholder="Max"
                      value={filters.priceMax}
                      onChange={handleFilterChange}
                      className="p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Bedrooms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Bedrooms
                  </label>
                  <select
                    name="minBeds"
                    value={filters.minBeds}
                    onChange={handleFilterChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option>Any</option>
                    <option>1</option>
                    <option>2</option>
                    <option>3</option>
                    <option>4</option>
                  </select>
                </div>
              </div>

              {/* Apply and Clear Filters */}
              <div className="mt-5 space-y-3">
                <button
                  onClick={handleApplyFilters}
                  disabled={loading}
                  className={`w-full ${loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#55C5D0] hover:bg-[#4AADB7]"
                    } text-white px-4 py-3 rounded-lg font-semibold shadow-md flex items-center justify-center`}
                >
                  {loading ? "Applying..." : "Apply Filters"}
                </button>

                <button
                  onClick={handleClearFilters}
                  disabled={loading}
                  className="w-full border-2 border-[#55C5D0] text-[#55C5D0] bg-white hover:bg-[#ecfeff] px-4 py-3 rounded-lg font-semibold shadow-sm flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </aside>

          {/* Property List */}
          <section className="flex-1">
            {loading ? (
              <div className="text-center py-20 text-gray-600 text-lg font-medium">
                Loading properties...
              </div>
            ) : error ? (
              <div className="text-center py-20 text-red-500 font-semibold">
                {error}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">
                    {properties.length} Properties Found
                  </h2>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {properties.map((property, index) => (
                    <div
                      key={property.id || index}
                      className="bg-white rounded-lg hover:shadow-lg shadow-md overflow-hidden w-full cursor-pointer"
                      onClick={() =>
                        navigate(`/property-view-listing/${property.id}`)
                      }
                    >
                      <div className="w-full h-48 sm:h-56 bg-gradient-to-br from-gray-200 to-gray-300 relative">
                        {property.images?.length || property.image ? (
                          <LazyImage
                            src={property.images?.[0]?.file || property.image}
                            alt={property.headline || "Property"}
                            className="w-full h-full object-cover"
                            style={{ minHeight: '14rem' }}
                          />
                        ) : (
                          <div className="text-gray-500 text-sm flex items-center justify-center h-full">
                            No Image Available
                          </div>
                        )}
                        <span className="absolute left-4 top-4 text-xs font-medium bg-white/90 text-gray-900 px-3 py-1 rounded-full shadow">
                          {property.status || property.listing_type || "Active"}
                        </span>
                      </div>

                      <div className="p-5 sm:p-6">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                          {property.title || property.name || "Untitled Property"}
                        </h3>

                        <div className="flex items-center text-gray-600 mb-2">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span className="text-sm">
                            {property.address ||
                              property.location ||
                              "No address available"}
                          </span>
                        </div>

                        <div className="text-xl sm:text-2xl font-bold text-black-600">
                          $
                          {property.price
                            ? Number(property.price).toLocaleString()
                            : "N/A"}
                        </div>

                        <div className="grid grid-cols-4 gap-2 sm:gap-4 mt-4">
                          <div className="text-center">
                            <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mx-auto mb-1">
                              <Bed className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="font-semibold text-lg text-gray-900">
                              {property.bedrooms || property.beds || 0}
                            </div>
                            <div className="text-xs text-gray-600">Beds</div>
                          </div>

                          <div className="text-center">
                            <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mx-auto mb-1">
                              <Bath className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="font-semibold text-lg text-gray-900">
                              {property.bathrooms || property.baths || 0}
                            </div>
                            <div className="text-xs text-gray-600">Baths</div>
                          </div>

                          <div className="text-center">
                            <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mx-auto mb-1">
                              <Car className="w-5 h-5 text-yellow-500" />
                            </div>
                            <div className="font-semibold text-lg text-gray-900">
                              {property.car_spaces || property.parkings || 0}
                            </div>
                            <div className="text-xs text-gray-600">Cars</div>
                          </div>

                          <div className="text-center">
                            <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mx-auto mb-1">
                              <Square className="w-5 h-5 text-pink-600" />
                            </div>
                            <div className="font-semibold text-lg text-gray-900">
                              {property.area || property.land_size || "N/A"}
                            </div>
                            <div className="text-xs text-gray-600">Land Size</div>
                          </div>
                        </div>

                        {/* Get in Touch Button - Small and positioned */}
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={(e) => handleGetInTouch(property, e)}
                            className="bg-[#55C5D0] hover:bg-[#4AADB7] text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center transition-colors"
                          >
                            <Phone className="w-3 h-3 mr-1.5" />
                            Get in Touch
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
